import React, { useState, useEffect } from "react";
import { Loading, Toaster } from "@/components";
import { useService } from "@/hooks/useService";
import { getCommerceProfiles, putCommercePropertyChange } from "@/services";
import {
  CommerceProfilesQueryDto,
  CommercePropertyChangeDto,
} from "@/services/mantenimientoComercios/types";
import {
  InlineEditTable,
  CustomPagination,
  CustomSearchBar,
  ConfirmSaveDialog,
  EditCommercialCategoryDialog,
} from "./components";
import {
  COMMECE_INFO_TABLE_COLUMNS,
  SEARCH_COLUMNS,
  DEFAIULT_PAGINATION_STATE,
  TOAST_DEFAULT_VALUE,
} from "./constants";
import { Paper } from "@mui/material";
import {
  CommerceInfo,
  PaginationResponse,
  PaginationState,
  ToastMessage,
} from "./types";
import { validateEmail } from "@/utils/emailValidation";

export default function CommerceInfoScreen() {
  const { loading, execute } = useService<
    PaginationResponse<CommerceInfo>,
    CommerceProfilesQueryDto
  >(getCommerceProfiles);
  const { execute: updateCommerceProperty, loading: updateLoading } =
    useService<boolean, CommercePropertyChangeDto>(putCommercePropertyChange);

  const [paginationState, setPaginationState] = useState<
    PaginationState<CommerceInfo>
  >(DEFAIULT_PAGINATION_STATE);
  const [currentPageItems, setCurrentPageItems] = useState<CommerceInfo[]>([]);

  const [editingField, setEditingField] = useState<{
    id: string;
    field: string;
  } | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [filterColumn, setFilterColumn] = useState<string>("cedRnc");
  const [inputValue, setInputValue] = useState<string>();
  const [searchQuery, setSearchQuery] = useState<CommerceProfilesQueryDto>({
    page: 1,
    pageSize: 5,
  });
  const [searchBarErrorMessage, setSearchBarErrorMessage] =
    useState<string>("");

  // Estado para el modal de categorías
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | null>(
    null,
  );
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Estado para el modal de confirmación
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: "edit" | "toggle" | "category";
    data?: any;
  } | null>(null);

  //Estado para toast de error
  const [toastProps, setToastProps] =
    useState<ToastMessage>(TOAST_DEFAULT_VALUE);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue === undefined) return;
      const currentValue = inputValue.trim();
      if (
        (currentValue !== "" && currentValue.length >= 3) ||
        inputValue === ""
      )
        handleInputSearchChange(inputValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    const fetchData = async () => {
      const result = await execute(searchQuery);

      if (result) {
        setPaginationState({
          pages: { 1: result.items },
          pagination: result.meta,
        });
        setCurrentPageItems(result.items);
      }
    };
    fetchData();
  }, []);

  const isEditing = (id: string, field: string) => {
    return editingField?.id === id && editingField?.field === field;
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditValue("");
  };

  const handleEdit = (id: string, field: string, currentValue: string) => {
    setEditingField({ id, field });
    setEditValue(currentValue);
  };

  const handleSave = () => {
    setPendingAction({ type: "edit" });
    setConfirmationOpen(true);
  };

  const handleToggleOverride = (id: string, currentValue: boolean) => {
    setPendingAction({ type: "toggle", data: { id, currentValue } });
    setConfirmationOpen(true);
  };

  const handleCancelConfirmation = () => {
    setConfirmationOpen(false);
    setPendingAction(null);
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
    setPendingAction(null);
  };

  const handleConfirmSave = async () => {
    if (!pendingAction) return;

    handleCloseConfirmation();

    let toastMessage,
      toastType: "error" | "success" = "error";

    switch (pendingAction.type) {
      case "edit":
        if (editingField) {
          const change_type =
            editingField.field === "commercialName" ? "change_name" : "change_email";
          const field_name =
            editingField.field === "commercialName"
              ? "Nombre Comercial"
              : "Correo Electrónico";

          const changeResult = await handleCommercePropertyChanged({
            changeType: change_type,
            id: editingField.id,
            newValue: editValue,
          });

          if (!changeResult) {
            toastType = "error";
            toastMessage = `Error al actualizar el valor del campo '${field_name}'`;
          } else {
            setEditingField(null);
            setEditValue("");
            toastType = "success";
            toastMessage = `Valor del campo '${field_name}' actualizado correctamente`;
          }
        }
        break;
      case "toggle": // Override Cardnet
        if (
          pendingAction.data &&
          pendingAction.data.id &&
          "currentValue" in pendingAction.data
        ) {
          const overrideCardnetResult = await handleCommercePropertyChanged({
            changeType: "change_override_cardnet",
            id: pendingAction.data.id,
            newValue: Boolean(!pendingAction.data.currentValue),
          });

          if (!overrideCardnetResult) {
            toastMessage = "Error al actualizar el valor de Override Cardnet";
          } else {
            toastType = "success";
            toastMessage =
              "Valor de Override Cardnet actualizado correctamente";
          }
        }
        break;
      case "category":
        setCategoryModalOpen(false);
        if (selectedMerchantId && selectedCategory) {
          const categoryChangeResult = await handleCommercePropertyChanged({
            changeType: "change_category",
            id: selectedMerchantId,
            newValue: selectedCategory,
          });

          setSelectedMerchantId(null);
          setSelectedCategory(null);

          if (!categoryChangeResult) {
            toastMessage = "Error al actualizar la categoría comercial";
          } else {
            toastType = "success";
            toastMessage = "Categoría comercial actualizada correctamente";
          }
        }
        break;
    }

    setPendingAction(null);
    handleToastMessage(toastType, toastMessage);
  };

  const handleSaveCategory = (newCategory: string) => {
    setSelectedCategory(newCategory);
    setPendingAction({ type: "category" });
    setConfirmationOpen(true);
  };

  const handlePageChange = async (newPage: number) => {
    if (
      newPage < 1 ||
      newPage > (paginationState.pagination.totalItems || 0) ||
      loading ||
      newPage === searchQuery.page
    ) {
      return;
    }

    const newQueryState = { ...searchQuery, page: newPage };

    if (paginationState.pages[newPage]) {
      setCurrentPageItems(paginationState.pages[newPage]);
      setSearchQuery(newQueryState);
      return;
    }

    await handleFilterUpdate(newQueryState);

    setSearchQuery(newQueryState);
  };

  const handleItemsPerPageChange = async (newItemsPerPage: number) => {
    const newQueryState = {
      ...searchQuery,
      pageSize: newItemsPerPage,
      page: 1,
    };

    await handleFilterUpdate(newQueryState, true);

    setSearchQuery(newQueryState);
  };

  const handleFilteredColumnChange = async (newFilterColumn: string) => {
    if (
      !searchQuery.documentNumber &&
      !searchQuery.commercialName &&
      !searchQuery.email &&
      !searchQuery.legalName
    )
      return;

    if (!inputValue || inputValue.trim() === "") return;

    let newSearchQuery: CommerceProfilesQueryDto;

    switch (newFilterColumn) {
      case "cedRnc":
        newSearchQuery = { ...searchQuery, documentNumber: inputValue };
        break;
      case "nombreLegal":
        newSearchQuery = { ...searchQuery, legalName: inputValue };
        break;
      case "nombreComercial":
        newSearchQuery = { ...searchQuery, commercialName: inputValue };
        break;
      case "correoElectronico":
        newSearchQuery = { ...searchQuery, email: inputValue };
        break;
      default:
        return;
    }

    await handleFilterUpdate(newSearchQuery, true);

    setSearchQuery(newSearchQuery);
  };

  const handleInputSearchChange = async (newInputValue: string) => {
    const inputCurrentValue = newInputValue.trim();

    if (
      inputCurrentValue.trim() === "" &&
      (searchQuery.documentNumber ||
        searchQuery.commercialName ||
        searchQuery.email ||
        searchQuery.legalName)
    ) {
      const newQueryState = {
        ...searchQuery,
        page: 1,
        documentNumber: undefined,
        commercialName: undefined,
        email: undefined,
        legalName: undefined,
      };

      await handleFilterUpdate(newQueryState, true);

      setSearchQuery(newQueryState);
      return;
    }

    let newSearchQuery: CommerceProfilesQueryDto;

    switch (filterColumn) {
      case "cedRnc":
        newSearchQuery = {
          ...searchQuery,
          page: 1,
          documentNumber: newInputValue,
        };
        break;
      case "nombreLegal":
        newSearchQuery = { ...searchQuery, page: 1, legalName: newInputValue };
        break;
      case "nombreComercial":
        newSearchQuery = {
          ...searchQuery,
          page: 1,
          commercialName: newInputValue,
        };
        break;
      case "correoElectronico":
        if (!validateEmail(newInputValue)) {
          setSearchBarErrorMessage("Correo electrónico inválido");
          return;
        }
        newSearchQuery = { ...searchQuery, page: 1, email: newInputValue };
        break;
      default:
        return;
    }

    await handleFilterUpdate(newSearchQuery, true);

    setSearchBarErrorMessage("");
    setSearchQuery(newSearchQuery);
  };

  const handleFilterUpdate = async (
    queryState: CommerceProfilesQueryDto,
    resetPaginationState: boolean = false,
  ) => {
    const result = await execute(queryState);

    if (result && result["items"]) {
      const page = result.meta.page;

      if (resetPaginationState) {
        setPaginationState({
          pages: { [page]: result.items },
          pagination: result.meta,
        });
      } else {
        setPaginationState((prev) => ({
          ...prev,
          pages: { ...prev.pages, [page]: result.items },
          pagination: result.meta,
        }));
      }

      setCurrentPageItems(result.items);
    } else {
      setCurrentPageItems([]);
      setPaginationState(DEFAIULT_PAGINATION_STATE);
    }
  };

  const handleToastMessage = (
    type: "error" | "success",
    errorMessage: string = "",
    open: boolean = true,
  ) => {
    setToastProps({
      message: errorMessage,
      type,
      open,
    });
  };
  const closeToast = () => {
    setToastProps((prev) => ({ ...prev, open: false }));
  };

  const handleRefreshPage = async () => {
    await handleFilterUpdate(searchQuery);
  };

  const handleCommercePropertyChanged = async (
    dto: CommercePropertyChangeDto,
  ): Promise<boolean> => {
    const result = await updateCommerceProperty(dto);

    if (typeof result === "boolean" && result) {
      await handleRefreshPage();
      return true;
    }
    return false;
  };

  return (
    <>
      <Paper sx={{ padding: 2, marginBottom: 2 }}>
        {(loading || updateLoading) && <Loading />}
        <CustomSearchBar
          searchColumns={SEARCH_COLUMNS}
          defaultFilterColumn={filterColumn}
          onFilterColumnChange={(column) => {
            setFilterColumn(column);
            if (!inputValue?.trim()) {
              setInputValue("");
              handleFilteredColumnChange(column);
            }
          }}
          onFilterValueChange={(value) => setInputValue(value)}
          inputValue={inputValue}
          hasError={!!searchBarErrorMessage}
          errorMessage={searchBarErrorMessage}
        />
        <InlineEditTable
          items={currentPageItems}
          columns={COMMECE_INFO_TABLE_COLUMNS}
          editValue={editValue}
          isEditing={(id, field) => isEditing(id, field)}
          onCancel={handleCancel}
          onEdit={handleEdit}
          onEditValueChange={(newValue: string) => setEditValue(newValue)}
          onSave={handleSave}
          onToggle={handleToggleOverride}
          onOpenCategoryModal={(id: string, currentCategory: string) => {
            setSelectedMerchantId(id);
            setSelectedCategory(currentCategory);
            setCategoryModalOpen(true);
          }}
        />
        <CustomPagination
          currentPage={searchQuery.page}
          totalPages={paginationState.pagination.totalPages || 1}
          itemsPerPage={paginationState.pagination.pageSize}
          setItemsPerPage={(newItemsPerPage) =>
            handleItemsPerPageChange(newItemsPerPage)
          }
          setCurrentPage={(newPage) => handlePageChange(newPage)}
          currentPageItems={currentPageItems.length}
          totalItems={paginationState.pagination.totalItems || 0}
        />
      </Paper>

      {/* Modal de confirmación */}
      <ConfirmSaveDialog
        confirmationOpen={confirmationOpen}
        handleCancelConfirmation={handleCancelConfirmation}
        handleConfirmSave={handleConfirmSave}
      />

      {/* Modal de edición de categoría comercial */}
      <EditCommercialCategoryDialog
        currentCategory={selectedCategory}
        categoryModalOpen={categoryModalOpen}
        setCategoryModalOpen={setCategoryModalOpen}
        handleSaveCategory={handleSaveCategory}
      />

      {/* Toat to notify error */}
      <Toaster
        show={toastProps.open}
        type={toastProps.type}
        text={toastProps.message}
        onClose={() => closeToast()}
      />
    </>
  );
}
